import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Tooltip } from "primereact/tooltip";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DateTimeFormatter, ZonedDateTime, ZoneId } from "@js-joda/core";
import { ColorKey, defaultHighlightColor } from "./EditBasicCellDialog";

const tooltipContent = `YYYY - ${T("time.years")}
MM - ${T("time.months")}
DD - ${T("time.days")}
HH - ${T("time.hours")}
mm - ${T("time.minutes")}
ss - ${T("time.seconds")}`;

export interface ICellConfig {
  type: CellType;
  value?: string | number | null;
  displayDatasetValueTime?: boolean;
  timePattern?: string;
  highlightColor?: {
    color?: string;
    bgColor?: string;
  };
}

export enum CellType {
  STRING = "string",
  NUMBER = "number",
  DATASET = "dataset",
  UNIT = "unit",
  SEPARATOR = "separator",
}

interface IEditDatasetCellDialogProps {
  isEditDatasetCellDialogVisible: boolean;
  setIsEditDatasetCellDialogVisible: (value: boolean) => void;
  selectedDatasetCell: ICellConfig;
  updateSelectedDatasetCell: (selectedDatasetCell: ICellConfig) => void;
}

const DEFAULT_TIME_PATTERN = "yyyy-MM-dd HH:mm:ss";

const EditDatasetCellDialog = ({
  isEditDatasetCellDialogVisible,
  setIsEditDatasetCellDialogVisible,
  selectedDatasetCell,
  updateSelectedDatasetCell,
}: IEditDatasetCellDialogProps) => {
  const [currentConfig, setCurrentConfig] = useState<ICellConfig>(selectedDatasetCell);

  const saveDatasetCellConfigChanges = () => {
    setIsEditDatasetCellDialogVisible(false);
    updateSelectedDatasetCell(currentConfig);
  };

  const handleEditDatasetCellDialogFooter = () => {
    return (
      <div className="flex flex-row justify-content-end">
        <Button
          label={T("action.cancel")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={() => setIsEditDatasetCellDialogVisible(false)}
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          onClick={saveDatasetCellConfigChanges}
          className="component-editor-button icon-green"
        />
      </div>
    );
  };

  const handleTimeValueSwitch = (e: InputSwitchChangeEvent) => {
    setCurrentConfig(prev => ({
      ...prev,
      displayDatasetValueTime: e.value,
      timePattern: e.value ? (prev.timePattern ?? DEFAULT_TIME_PATTERN) : undefined,
    }));
  };

  const handleColorChange = (key: ColorKey, value: string) => {
    setCurrentConfig(prev => ({
      ...prev,
      highlightColor: {
        ...prev.highlightColor,
        [key]: value,
      },
    }));
  };

  const getFormattedTime = () => {
    try {
      if (!currentConfig.timePattern) return "";
      const now = ZonedDateTime.now(ZoneId.systemDefault());
      const formatter = DateTimeFormatter.ofPattern(currentConfig.timePattern);
      return now.format(formatter);
    } catch (error) {
      return "Invalid pattern";
    }
  };

  return (
    <Dialog
      header={T("header.edit-dataset-cell")}
      footer={handleEditDatasetCellDialogFooter()}
      visible={isEditDatasetCellDialogVisible}
      onHide={() => setIsEditDatasetCellDialogVisible(false)}
      style={{ width: "500px" }}
    >
      <div className="flex flex-column gap-3">
        <DatasetDropdown
          value={currentConfig.value as string}
          onChange={value => setCurrentConfig({ ...currentConfig, value })}
        />
        <div className="flex flex-column gap-2">
          <label htmlFor="displayDatasetValueTime">{T("label.display-dataset-value-time")}</label>
          <InputSwitch
            checked={currentConfig.displayDatasetValueTime ?? false}
            onChange={handleTimeValueSwitch}
          />
        </div>
        {currentConfig.displayDatasetValueTime && (
          <div className="flex flex-column gap-2">
            <label htmlFor="timePattern" className="tooltip-label">
              {T("label.time-pattern")}
              <i id="timePatternTooltip" className="pi pi-info-circle text-primary ml-2" />
            </label>
            <Tooltip target="#timePatternTooltip" content={tooltipContent} position="top" />
            <InputText
              id="timePattern"
              value={currentConfig.timePattern ?? ""}
              onChange={e => setCurrentConfig({ ...currentConfig, timePattern: e.target.value })}
            />
          </div>
        )}
        <div className="flex flex-row justify-content-around">
          <div className="flex flex-column gap-2 align-items-left">
            <span>{T("word.text-color")}</span>
            <ColorPicker
              value={currentConfig.highlightColor?.color ?? defaultHighlightColor.defaultTextColor}
              onValueChange={e => handleColorChange(ColorKey.COLOR, e.target.value)}
            />
          </div>
          <div className="flex flex-column gap-2 align-items-left">
            <span>{T("word.background-color")}</span>
            <ColorPicker
              value={
                currentConfig.highlightColor?.bgColor ??
                defaultHighlightColor.defaultBackgroundColor
              }
              onValueChange={e => handleColorChange(ColorKey.BG_COLOR, e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-content-center mt-3">
          <span
            className="p-2"
            style={{
              color: currentConfig.highlightColor?.color,
              backgroundColor: currentConfig.highlightColor?.bgColor,
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            {currentConfig.displayDatasetValueTime ? getFormattedTime() : T("word.preview")}
          </span>
        </div>
      </div>
    </Dialog>
  );
};

export default EditDatasetCellDialog;
